import React, { useState, useEffect, useRef, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Input from 'components/Input'
import Select from 'components/Select/selectAbout'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import InfoSmallIcon from 'assets/icons/info-small-icon'
import RadioButton from 'components/RadioButton'
import Textarea from 'components/Textarea'
import Loader from 'components/Loader'
import StateEvaluationSection from './StateEvaluationFormSection'
import {
  getStateCategory,
  getStateCategorySource,
  getStateCategoryTyps,
  requestSaveStateEvaluation,
  getStateEvaluationById,
  updateStateEvaluation,
  requestTriggerStateBenchmark,
  updateTriggerStateBenchmark,
  getTriggerStateBenchmark,
  getMonitoringParameter
} from 'api/monitoring'
import { PageTitle, PageSubtitle, PageSubtitleWrapper, TooltipHintWrapper } from 'flow/siteProposal/pages/about/style'
import { StateFormContainer, StateFormItemWrapper, FullWidthWrapper } from './style'
import { useParams } from 'react-router-dom'

function SpeciesStateForm({
  triggerItem,
  onEvaluationCreated,
  evaluationId,
  isSiteStateValid = true,
  isEditable,
  isRevaluation
}) {
  const intl = useIntl()
  const { id: siteId } = useParams()
  const [evaluationType, setEvaluationType] = useState(null)
  const [benchmarkValue, setBenchmarkValue] = useState('')
  const [isChangeGenuine, setIsChangeGenuine] = useState(true)
  const [speciesState, setSpeciesState] = useState(null)
  const [ecosystemState, setEcosystemState] = useState(null)
  const [inferringMethod, setInferringMethod] = useState(null)
  const [justification, setJustification] = useState('')
  const [stateOptions, setStateOptions] = useState([])
  const [inferringOptions, setInferringOptions] = useState([])
  const [evaluationTypeOptionsApi, setEvaluationTypeOptionsApi] = useState([])
  const [monitoringParameterOptions, setMonitoringParameterOptions] = useState([])
  const [localEvaluationId, setLocalEvaluationId] = useState(null)
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false)
  const [initialData, setInitialData] = useState(null)
  const [localTriggerBenchmarkId, setLocalTriggerBenchmarkId] = useState(
    triggerItem?.raw?.triggerStateBenchmark?.[0]?.id || triggerItem?.raw?.benchmark?.id || null
  )
  const [isFormLoading, setIsFormLoading] = useState(true)
  const [prefillLoaded, setPrefillLoaded] = useState(false)
  const [triggerBenchmarkLoaded, setTriggerBenchmarkLoaded] = useState(false)
  const [isTopFieldsEdited, setIsTopFieldsEdited] = useState(false)

  const hasUserEditedNoFieldsRef = useRef(false)
  const isTopFieldsEditedRef = useRef(false)
  const savedNoSectionDataRef = useRef(null)
  const MAX_TEXTBOX_CHAR_LIMIT = 255
  useEffect(() => {
    isTopFieldsEditedRef.current = false
    hasUserEditedNoFieldsRef.current = false
    savedNoSectionDataRef.current = null
    setIsTopFieldsEdited(false)
  }, [triggerItem?.raw?.id])

  const isEditMode = Boolean(evaluationId)
  const saveTimeoutRefBenchmark = useRef(null)
  const saveTimeoutRef = useRef(null)
  const inputSaveDelayTime = 500

  const [formData, setFormData] = useState({
    name: '',
    taxonomicGroup: '',
    monitoringCount: '',
    baselineYear: '',
    benchmark: '',
    monitoringParameter: '',
    isCustomParameter: false,
    customMonitoringParameter: '',
    previousParameter: null,
    justifyBenchmark: '',
    editableBenchmark: '',
    editableMonitoringParameter: ''
  })

  const formDataRef = useRef(formData)
  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  useEffect(() => {
    if (!dropdownsLoaded) return

    if (isEditMode) {
      if (prefillLoaded && triggerBenchmarkLoaded) {
        setIsFormLoading(false)
      }
    } else {
      setIsFormLoading(false)
    }
  }, [dropdownsLoaded, prefillLoaded, triggerBenchmarkLoaded, isEditMode])

  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const speciesId = triggerItem?.raw?.siteSpeciesId || triggerItem?.raw?.id || triggerItem?.id || null

        const [stateRes, inferringRes, typeRes, monitoringParamRes] = await Promise.all([
          getStateCategory(),
          getStateCategorySource(),
          getStateCategoryTyps(),
          getMonitoringParameter(speciesId, siteId)
        ])

        const mapOptions = (arr) => {
          const list = Array.isArray(arr)
            ? arr
            : Array.isArray(arr?.data)
              ? arr.data
              : Array.isArray(arr?.data?.data)
                ? arr.data.data
                : []
          return list
            .filter((item) => {
              const name = item.benchmark_parameter || item.name
              return name !== undefined && name !== null && String(name).trim() !== ''
            })
            .map((item) => {
              const name = item.benchmark_parameter || item.name
              return {
                label: name,
                value: name,
                ...item
              }
            })
        }

        const mapOptionsId = (arr) =>
          Array.isArray(arr)
            ? arr.map((item) => ({ label: item.name, value: item.id }))
            : Array.isArray(arr?.data)
              ? arr.data.map((item) => ({ label: item.name, value: item.id }))
              : Array.isArray(arr?.data?.data)
                ? arr.data.data.map((item) => ({ label: item.name, value: item.id }))
                : []

        setStateOptions(mapOptionsId(stateRes))
        setInferringOptions(mapOptionsId(inferringRes))
        setEvaluationTypeOptionsApi(mapOptionsId(typeRes))

        const paramsOptions = mapOptions(monitoringParamRes)
        setMonitoringParameterOptions(paramsOptions)

        setDropdownsLoaded(true)
      } catch (err) {
        console.error('Error fetching dropdowns:', err)
      }
    }

    fetchDropdownData()
  }, [])

  useEffect(() => {
    if (!isEditMode || !dropdownsLoaded) return

    async function fetchPrefill() {
      try {
        const res = await getStateEvaluationById(evaluationId)
        const data = res?.data?.data || res?.data || res

        const evalType = data.state_evaluation_type_id ? Number(data.state_evaluation_type_id) : null
        const genuine =
          data.genuine_change === true || data.genuine_change === 'true'
            ? true
            : data.genuine_change === false || data.genuine_change === 'false'
              ? false
              : true

        const mapOption = (options, id) => options.find((o) => String(o.value) === String(id)) || null
        setEvaluationType(evalType)
        setIsChangeGenuine(genuine)
        setBenchmarkValue(data.update_estimate)
        setSpeciesState(data.state_category_extent_id)
        setEcosystemState(data.state_category_condition_id)
        setInferringMethod(mapOption(inferringOptions, data.state_category_source_id))
        setJustification(data.state_category_justification ?? '')

        setFormData((prev) => {
          const otherOption = monitoringParameterOptions.find((o) => o.value?.toLowerCase().includes('other'))?.value
          const hasParam = data.benchmark_parameter !== undefined && data.benchmark_parameter !== null
          const isCustom = Boolean(
            hasParam &&
            data.benchmark_parameter !== '' &&
            !monitoringParameterOptions.find((o) => o.value === data.benchmark_parameter)
          )

          const mergedPrevParam = data.previous_parameter ?? prev.previousParameter

          return {
            ...prev,
            benchmark: isTopFieldsEditedRef.current
              ? prev.benchmark
              : (triggerItem?.raw?.benchmark_estimate ?? data.benchmark_estimate ?? prev.benchmark),
            monitoringParameter: isTopFieldsEditedRef.current
              ? prev.monitoringParameter
              : (triggerItem?.raw?.benchmark_parameter ?? data.benchmark_parameter ?? prev.monitoringParameter),

            editableMonitoringParameter: hasUserEditedNoFieldsRef.current
              ? prev.editableMonitoringParameter
              : hasParam && mergedPrevParam !== true
                ? isCustom
                  ? otherOption
                  : data.benchmark_parameter
                : prev.editableMonitoringParameter,

            isCustomParameter: hasUserEditedNoFieldsRef.current
              ? prev.isCustomParameter
              : hasParam && mergedPrevParam !== true
                ? isCustom
                : prev.isCustomParameter,

            customMonitoringParameter: hasUserEditedNoFieldsRef.current
              ? prev.customMonitoringParameter
              : hasParam && mergedPrevParam !== true
                ? isCustom
                  ? data.benchmark_parameter
                  : ''
                : prev.customMonitoringParameter,

            baselineYear: data.baseline_year ?? prev.baselineYear,
            previousParameter: mergedPrevParam,

            justifyBenchmark: hasUserEditedNoFieldsRef.current
              ? prev.justifyBenchmark
              : (data.justify_benchmark ?? prev.justifyBenchmark),

            editableBenchmark: hasUserEditedNoFieldsRef.current
              ? prev.editableBenchmark
              : data.benchmark_estimate != null
                ? String(data.benchmark_estimate)
                : prev.editableBenchmark
          }
        })

        // after setFormData
        const hadExistingData = Boolean(
          data.benchmark_parameter || data.benchmark_estimate != null || data.justify_benchmark
        )
        if (hadExistingData) {
          hasUserEditedNoFieldsRef.current = true
        }

        setLocalEvaluationId(data.id ?? data?.evaluation_id ?? localEvaluationId)
        setInitialData(data)
        const tri = triggerItem?.raw?.triggerStateBenchmark?.[0]?.id || triggerItem?.raw?.benchmark?.id || null
        if (tri) setLocalTriggerBenchmarkId(tri)
      } catch (err) {
        console.error('Prefill fetch error:', err)
      } finally {
        setPrefillLoaded(true)
      }
    }

    fetchPrefill()
  }, [evaluationId, dropdownsLoaded, isEditMode])

  useEffect(() => {
    if (!dropdownsLoaded) return
    if (!localTriggerBenchmarkId) {
      setTriggerBenchmarkLoaded(true)
      return
    }
    setTriggerBenchmarkLoaded(true)
    let mounted = true

    async function fetchTriggerBenchmark() {
      try {
        const res = await getTriggerStateBenchmark(localTriggerBenchmarkId)
        const data = res?.data || res
        if (!mounted) return

        setFormData((prev) => {
          const otherOption = monitoringParameterOptions.find((o) => o.value?.toLowerCase().includes('other'))?.value
          const hasParam = data.benchmark_parameter !== undefined && data.benchmark_parameter !== null
          const isCustom = Boolean(
            hasParam &&
            data.benchmark_parameter !== '' &&
            !monitoringParameterOptions.find((o) => o.value === data.benchmark_parameter)
          )
          const firstEvaluationWithBenchmarkParameter =
            !isRevaluation && !isEditMode && triggerItem?.raw?.benchmark_parameter
          const mergedPrevParam = firstEvaluationWithBenchmarkParameter
            ? true
            : (data.previous_parameter ?? prev.previousParameter)

          return {
            ...prev,
            benchmark: isTopFieldsEditedRef.current
              ? prev.benchmark
              : (triggerItem?.raw?.benchmark_estimate ?? data.benchmark_estimate ?? prev.benchmark),
            monitoringParameter: isTopFieldsEditedRef.current
              ? prev.monitoringParameter
              : (triggerItem?.raw?.benchmark_parameter ?? data.benchmark_parameter ?? prev.monitoringParameter),

            editableMonitoringParameter: hasUserEditedNoFieldsRef.current
              ? prev.editableMonitoringParameter
              : hasParam && mergedPrevParam !== true
                ? isCustom
                  ? otherOption
                  : data.benchmark_parameter
                : prev.editableMonitoringParameter,

            isCustomParameter: hasUserEditedNoFieldsRef.current
              ? prev.isCustomParameter
              : hasParam && mergedPrevParam !== true
                ? isCustom
                : prev.isCustomParameter,

            customMonitoringParameter: hasUserEditedNoFieldsRef.current
              ? prev.customMonitoringParameter
              : hasParam && mergedPrevParam !== true
                ? isCustom
                  ? data.benchmark_parameter
                  : ''
                : prev.customMonitoringParameter,

            baselineYear: data.baseline_year ?? prev.baselineYear,
            previousParameter: mergedPrevParam,

            justifyBenchmark: hasUserEditedNoFieldsRef.current
              ? prev.justifyBenchmark
              : (data.justify_benchmark ?? prev.justifyBenchmark),

            editableBenchmark: hasUserEditedNoFieldsRef.current
              ? prev.editableBenchmark
              : data.benchmark_estimate != null
                ? String(data.benchmark_estimate)
                : prev.editableBenchmark
          }
        })

        const hadExistingData = Boolean(
          data.benchmark_parameter || data.benchmark_estimate != null || data.justify_benchmark
        )
        if (hadExistingData) {
          hasUserEditedNoFieldsRef.current = true
        }

        if (data.id || data?.data?.id) {
          setLocalTriggerBenchmarkId(data.id || data?.data?.id)
        }
      } catch (err) {
        console.error('Failed to prefetch trigger benchmark:', err)
      } finally {
        if (mounted) setTriggerBenchmarkLoaded(true)
      }
    }

    fetchTriggerBenchmark()

    return () => {
      mounted = false
    }
  }, [localTriggerBenchmarkId, dropdownsLoaded])

  useEffect(() => {
    if (!triggerItem?.raw) return

    const b = triggerItem.raw
    setFormData((prev) => {
      const otherOption = monitoringParameterOptions.find((o) => o.value?.toLowerCase().includes('other'))?.value
      const hasParam = b.benchmark_parameter !== undefined && b.benchmark_parameter !== null
      const isCustom = Boolean(hasParam && !monitoringParameterOptions.find((o) => o.value === b.benchmark_parameter))
      return {
        ...prev,
        name: triggerItem.name || '',
        taxonomicGroup: triggerItem.raw.class_name || '',
        monitoringCount: b.number_evaluations || '0',
        baselineYear: b.baseline_year ?? '',
        benchmark: isTopFieldsEditedRef.current ? prev.benchmark : (b.benchmark_estimate ?? ''),
        monitoringParameter: isTopFieldsEditedRef.current
          ? prev.monitoringParameter
          : (b.benchmark_parameter ?? prev.monitoringParameter),
        editableMonitoringParameter: hasUserEditedNoFieldsRef.current
          ? prev.editableMonitoringParameter
          : hasParam
            ? isCustom
              ? otherOption
              : b.benchmark_parameter
            : prev.editableMonitoringParameter,
        isCustomParameter: hasUserEditedNoFieldsRef.current
          ? prev.isCustomParameter
          : hasParam
            ? isCustom
            : prev.isCustomParameter,
        customMonitoringParameter: hasUserEditedNoFieldsRef.current
          ? prev.customMonitoringParameter
          : hasParam
            ? isCustom
              ? b.benchmark_parameter
              : ''
            : prev.customMonitoringParameter,
        previousParameter: b.previous_parameter ?? null,
        justifyBenchmark: hasUserEditedNoFieldsRef.current ? prev.justifyBenchmark : (b.justify_benchmark ?? ''),
        editableBenchmark: hasUserEditedNoFieldsRef.current ? prev.editableBenchmark : (b.benchmark_estimate ?? '')
      }
    })

    setLocalTriggerBenchmarkId(b.id)
  }, [triggerItem, dropdownsLoaded])

  const scheduleSaveBenchmark = useCallback(
    (field, value) => {
      if (field === 'benchmark_parameter_select') {
        const valStr = value?.value || ''
        if (valStr === (formDataRef.current.editableMonitoringParameter || '')) return
      }
      if (field === 'custom_benchmark_parameter' && value === formDataRef.current.customMonitoringParameter) return
      if (field === 'editable_benchmark_estimate' && value === formDataRef.current.editableBenchmark) return
      if (field === 'justify_benchmark' && value === formDataRef.current.justifyBenchmark) return

      setFormData((prev) => ({ ...prev, [field]: value }))

      if (field === 'benchmark_parameter_select') {
        hasUserEditedNoFieldsRef.current = true
        const selected = value
        const valStr = selected?.value || ''
        const isOther = valStr.toLowerCase().includes('other')

        const newEstimate = isOther ? '0' : (selected?.benchmark_estimate ?? '')
        const newJustify = isOther ? '' : (selected?.justify_benchmark ?? '')

        setFormData((p) => ({
          ...p,
          editableMonitoringParameter: valStr,
          isCustomParameter: isOther,
          editableBenchmark: newEstimate,
          justifyBenchmark: newJustify
        }))
      }
      if (field === 'custom_benchmark_parameter') {
        hasUserEditedNoFieldsRef.current = true
        setFormData((p) => ({ ...p, customMonitoringParameter: value }))
      }
      if (field === 'editable_benchmark_estimate') {
        hasUserEditedNoFieldsRef.current = true
        setFormData((p) => ({ ...p, editableBenchmark: value }))
      }
      if (field === 'previous_parameter') {
        setFormData((p) => {
          const next = { ...p, previousParameter: value }

          if (value === true && p.previousParameter === false) {
            // No,Yes save whatever user typed in No section
            if (hasUserEditedNoFieldsRef.current) {
              // only save if user actually typed something
              savedNoSectionDataRef.current = {
                editableBenchmark: p.editableBenchmark,
                editableMonitoringParameter: p.editableMonitoringParameter,
                customMonitoringParameter: p.customMonitoringParameter,
                isCustomParameter: p.isCustomParameter,
                justifyBenchmark: p.justifyBenchmark
              }
            }
          }

          if (value === false && p.previousParameter !== false) {
            if (!hasUserEditedNoFieldsRef.current && !savedNoSectionDataRef.current) {
              // First time clicking No, user never typed anything EMPTY
              next.editableBenchmark = '0'
              next.editableMonitoringParameter = ''
              next.customMonitoringParameter = ''
              next.isCustomParameter = false
              next.justifyBenchmark = ''
            } else if (savedNoSectionDataRef.current) {
              // User typed before, then switched to Yes, now back to No RESTORE
              next.editableBenchmark = savedNoSectionDataRef.current.editableBenchmark
              next.editableMonitoringParameter = savedNoSectionDataRef.current.editableMonitoringParameter
              next.customMonitoringParameter = savedNoSectionDataRef.current.customMonitoringParameter
              next.isCustomParameter = savedNoSectionDataRef.current.isCustomParameter
              next.justifyBenchmark = savedNoSectionDataRef.current.justifyBenchmark
            } else {
              // hasUserEditedNoFieldsRef=true but no savedData
            }
          }

          return next
        })
      }
      if (field === 'justify_benchmark') {
        hasUserEditedNoFieldsRef.current = true
        setFormData((p) => ({ ...p, justifyBenchmark: value }))
      }

      if (saveTimeoutRefBenchmark.current) clearTimeout(saveTimeoutRefBenchmark.current)

      saveTimeoutRefBenchmark.current = setTimeout(async () => {
        try {
          const payload = {}
          if (field === 'benchmark_parameter') {
            const isOther = String(value || '')
              .toLowerCase()
              .includes('other')
            if (!isOther) payload.benchmark_parameter = value
            else payload.benchmark_parameter = ''
          } else if (field === 'benchmark_parameter_select') {
            const selected = value
            const valStr = String(selected?.value || '')
            const isOther = valStr.toLowerCase().includes('other')
            if (!isOther) {
              payload.benchmark_parameter = valStr
              payload.benchmark_estimate = selected?.benchmark_estimate ?? 0
              payload.justify_benchmark = selected?.justify_benchmark ?? ''
            } else {
              payload.benchmark_parameter = ''
              payload.benchmark_estimate = 0
              payload.justify_benchmark = ''
            }
          } else if (field === 'custom_benchmark_parameter') {
            const exactMatch = monitoringParameterOptions.find(
              (o) => o.value && value && o.value.trim().toLowerCase() === value.trim().toLowerCase()
            )
            if (value?.trim().toLowerCase() === 'other') return
            payload.benchmark_parameter = exactMatch ? exactMatch.value : value
          } else if (field === 'benchmark_estimate' || field === 'editable_benchmark_estimate') {
            payload.benchmark_estimate = value
          } else if (field === 'previous_parameter') {
            if (value === true) {
              payload.previous_parameter = true
              payload.benchmark_estimate = Number(formDataRef.current.benchmark) || 0
              payload.benchmark_parameter = formDataRef.current.monitoringParameter || ''
              payload.justify_benchmark = initialData?.justify_benchmark ?? ''
            } else {
              payload.previous_parameter = false
              payload.benchmark_estimate = Number(formDataRef.current.editableBenchmark) || 0
              payload.benchmark_parameter = formDataRef.current.isCustomParameter
                ? formDataRef.current.customMonitoringParameter || ''
                : formDataRef.current.editableMonitoringParameter || ''
              payload.justify_benchmark = formDataRef.current.justifyBenchmark || ''
            }
          } else if (field === 'justify_benchmark') {
            payload.justify_benchmark = value
          }

          const bottomFields = [
            'benchmark_parameter_select',
            'custom_benchmark_parameter',
            'justify_benchmark',
            'editable_benchmark_estimate'
          ]
          if (bottomFields.includes(field)) {
            payload.previous_parameter = false
          }

          if (!payload || Object.keys(payload).length === 0) return

          if (localTriggerBenchmarkId) {
            const updatePayload = {
              ...payload,
              is_added: true
            }
            await updateTriggerStateBenchmark(localTriggerBenchmarkId, updatePayload)
          } else {
            try {
              const createPayload = {
                ...payload,
                is_added: false
              }
              if (triggerItem?.type === 'Species') createPayload.siteSpeciesId = triggerItem.raw?.id || triggerItem.id
              else createPayload.siteEcosystemId = triggerItem.raw?.id || triggerItem.id
              const created = await requestTriggerStateBenchmark([createPayload])
              const newId = Array.isArray(created)
                ? created[0]?.id || created[0]?.data?.id
                : created?.id || created?.data?.id
              if (newId) setLocalTriggerBenchmarkId(newId)
            } catch (e) {
              console.warn('Failed to create trigger benchmark record:', e)
            }
          }
        } catch (err) {
          console.error('Benchmark autosave failed:', err)
        }
      }, inputSaveDelayTime)
    },
    [localTriggerBenchmarkId, triggerItem, monitoringParameterOptions]
  )

  const radioOptions = [
    { label: intl.formatMessage({ id: 'changeGenuineYes' }), value: true },
    { label: intl.formatMessage({ id: 'changeGenuineNo' }), value: false }
  ]

  const previousParameterOptions = [
    { label: intl.formatMessage({ id: 'previousParameterYes' }), value: true },
    { label: intl.formatMessage({ id: 'previousParameterNo' }), value: false }
  ]

  const isUpdate = localEvaluationId || evaluationId

  const buildCreatePayload = (data) => {
    const benchmarkId = triggerItem?.benchmarkId || triggerItem?.id || triggerItem?.raw?.id
    return {
      triggerstate_benchmark_id: Number(benchmarkId),
      state_evaluation_type_id: data.state_evaluation_type_id ?? evaluationType,
      genuine_change: isChangeGenuine !== null && isChangeGenuine !== undefined ? isChangeGenuine : true
    }
  }

  const scheduleSave = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }))

      if (field === 'state_evaluation_type_id') setEvaluationType(value)
      if (field === 'genuine_change') setIsChangeGenuine(value)
      if (field === 'update_estimate') setBenchmarkValue(value)
      if (field === 'state_category_extent_id') setSpeciesState(value)
      if (field === 'state_category_condition_id') setEcosystemState(value)
      if (field === 'state_category_justification') setJustification(value)

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        const benchmarkId = triggerItem?.benchmarkId || triggerItem?.id || triggerItem?.raw?.id
        try {
          const currentEvalType =
            field === 'state_evaluation_type_id' ? value : (evaluationType ?? initialData?.state_evaluation_type_id)

          const payload = {}

          if (field === 'state_category_extent_id') {
            payload.state_category_extent_id = Number(value)
          } else if (field === 'update_estimate') {
            if (Number(currentEvalType) === 1) {
              if (value !== undefined && value !== null && value !== '') payload.update_estimate = Number(value)
            }
          } else if (field === 'state_evaluation_type_id') {
            payload.state_evaluation_type_id = Number(value)
          } else if (field === 'state_category_condition_id') {
            payload.state_category_condition_id = Number(value)
          } else if (field === 'state_category_source_id') {
            payload.state_category_source_id = Number(value)
          } else if (field === 'genuine_change') {
            payload.genuine_change = value !== null && value !== undefined ? value : true
            if (Number(currentEvalType) === 1) {
              if (benchmarkValue !== undefined && benchmarkValue !== null && benchmarkValue !== '') {
                payload.update_estimate = Number(benchmarkValue)
              }
            }
          } else {
            payload[field] = value
          }

          payload.triggerstate_benchmark_id = Number(benchmarkId)

          if (isUpdate) {
            const keys = Object.keys(payload).filter((k) => k !== 'triggerstate_benchmark_id')
            if (keys.length === 0) {
              return
            }

            await updateStateEvaluation(localEvaluationId || evaluationId, payload)
          } else {
            const createPayload = buildCreatePayload({ state_evaluation_type_id: evaluationType ?? value })
            const createRes = await requestSaveStateEvaluation(createPayload)
            const evaluationIdFromResponse = createRes?.id || createRes?.data?.id
            if (evaluationIdFromResponse) {
              setLocalEvaluationId(evaluationIdFromResponse)
              onEvaluationCreated?.(evaluationIdFromResponse)
            }
          }
        } catch (err) {
          console.error('Autosave failed:', err)
        }
      }, inputSaveDelayTime)
    },
    [
      evaluationType,
      initialData,
      localEvaluationId,
      evaluationId,
      isUpdate,
      speciesState,
      triggerItem,
      onEvaluationCreated,
      benchmarkValue
    ]
  )

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      if (saveTimeoutRefBenchmark.current) {
        clearTimeout(saveTimeoutRefBenchmark.current)
        saveTimeoutRefBenchmark.current = null
      }
    }
  }, [])

  const isParamBlank = !formData.monitoringParameter || String(formData.monitoringParameter).trim() === ''
  const isZero = Number(formData.benchmark) === 0
  const isBenchmarkBlank = formData.benchmark === '' || formData.benchmark === null || formData.benchmark === undefined
  const value = formData.editableBenchmark
  const isEmpty = value === null || value === undefined || String(value).trim() === ''
  const firstEvaluationWithOutBenchmarkParameter =
    !isRevaluation && !isEditMode && !triggerItem?.raw?.benchmark_parameter
  return (
    <div>
      <PageTitle style={{ marginTop: '35px' }}>
        <FormattedMessage id="benchmarkDetails" />
      </PageTitle>

      <PageSubtitleWrapper>
        <PageSubtitle>
          <FormattedMessage id="benchmarksubhead" />
        </PageSubtitle>

        <Tooltip
          style={{ zIndex: 1000 }}
          placement="bottom"
          trigger={['hover']}
          align={{ offset: [0, 16] }}
          overlay={{
            label: 'benchmarkDetails',
            infoTooltip: 'benchmarktooltip'
          }}
          type="big"
        >
          <TooltipHintWrapper>
            <InfoIcon />
          </TooltipHintWrapper>
        </Tooltip>
      </PageSubtitleWrapper>

      {isFormLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 20px' }}>
          <Loader />
        </div>
      ) : (
        <>
          {/* 2 COLUMN SECTION */}
          <StateFormContainer>
            <StateFormItemWrapper>
              <Input label="biodiversitytriggerElement" placeholder="Centrolene azulae" value={formData.name} disabled />
            </StateFormItemWrapper>

            {(triggerItem?.raw?.category === 'species' || triggerItem?.type === 'Species') && (
              <StateFormItemWrapper>
                <Input label="bioTaxonomicGroup" placeholder="Aves" value={formData.taxonomicGroup} disabled />
              </StateFormItemWrapper>
            )}

            <StateFormItemWrapper>
              <Input label="bioNumberEvaluation" placeholder="Value" value={formData.monitoringCount} disabled />
            </StateFormItemWrapper>

            <StateFormItemWrapper>
              <Select
                label="bioBaselineYear"
                placeholder="Select Year"
                value={
                  formData.baselineYear
                    ? { label: formData.baselineYear.toString(), value: formData.baselineYear.toString() }
                    : null
                }
                disabled
              />
            </StateFormItemWrapper>

            {triggerItem?.raw?.category === 'ecosystem' && (
              <>
                <StateFormItemWrapper>
                  <Input
                    label="bioMonitoringPerameter"
                    placeholder="enterParameter"
                    infoTooltip="bioMonitoringPerameterToolTip"
                    value={formData.monitoringParameter}
                    disabled
                    field="benchmark_parameter"
                    error={
                      isSiteStateValid || formData.monitoringParameter
                        ? { field: '', error: '' }
                        : {
                          field: 'benchmark_parameter',
                          error: 'pleaseCompleteThisField'
                        }
                    }
                  />
                </StateFormItemWrapper>

                <StateFormItemWrapper>
                  <Input
                    label="bioBenchmarkValue"
                    placeholder="Value"
                    infoTooltip="bioBenchmarkValueToolTip"
                    value={formData.benchmark}
                    disabled
                    field="benchmark_estimate"
                    error={
                      isSiteStateValid || formData.benchmark > 0
                        ? { field: '', error: '' }
                        : {
                          field: 'benchmark_estimate',
                          error: 'pleaseCompleteThisField'
                        }
                    }
                  />
                </StateFormItemWrapper>
              </>
            )}
          </StateFormContainer>
          {triggerItem?.raw?.category === 'species' && (
            <>
              <FullWidthWrapper style={{ marginTop: '20px', marginBottom: '50px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '1px' }}>
                  <FormattedMessage id="isPreviouslyStatedMonitoringParameter" />
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    placement="bottom"
                    trigger={['hover']}
                    align={{ offset: [0, 16] }}
                    overlay={{
                      label: 'isPreviouslyStatedMonitoringParameter',
                      infoTooltip: 'isPreviouslyStatedMonitoringParameterTooltip'
                    }}
                    type="big"
                  >
                    <TooltipHintWrapper>
                      <InfoSmallIcon />
                    </TooltipHintWrapper>
                  </Tooltip>
                </div>
                <div style={{ marginTop: '12px' }}>
                  {previousParameterOptions.map((option) => (
                    <RadioButton
                      key={String(option.value)}
                      name="previousParameter"
                      value={option.value}
                      label={option.label}
                      checked={formData.previousParameter === option.value}
                      disabled={!isEditable || firstEvaluationWithOutBenchmarkParameter}
                      onChange={(value) => {
                        scheduleSaveBenchmark('previous_parameter', value)
                      }}
                      dataTest={`previousParameter-${option.value}`}
                    />
                  ))}
                </div>
              </FullWidthWrapper>

              {formData.previousParameter === true && (
                <StateFormContainer style={{ paddingBottom: '20px' }}>
                  <StateFormItemWrapper>
                    <Input
                      label="bioMonitoringPerameter"
                      placeholder="enterParameter"
                      infoTooltip="bioMonitoringPerameterToolTip"
                      value={formData.monitoringParameter}
                      onChange={(e) => {
                        setIsTopFieldsEdited(true)
                        isTopFieldsEditedRef.current = true
                        setFormData((prev) => ({ ...prev, monitoringParameter: e.target.value }))
                        scheduleSaveBenchmark('benchmark_parameter', e.target.value)
                      }}
                      disabled={(!isTopFieldsEdited && !isParamBlank) || !isEditable}
                      field="benchmark_parameter"
                      error={
                        isSiteStateValid || formData.monitoringParameter
                          ? { field: '', error: '' }
                          : {
                            field: 'benchmark_parameter',
                            error: 'pleaseCompleteThisField'
                          }
                      }
                    />
                  </StateFormItemWrapper>

                  <StateFormItemWrapper>
                    <Input
                      label="bioBenchmarkValue"
                      placeholder="Value"
                      marginBottom="11px"
                      infoTooltip="bioBenchmarkValueToolTip"
                      value={isParamBlank && isZero && !isTopFieldsEdited ? '0' : formData.benchmark}
                      onChange={(e) => {
                        setIsTopFieldsEdited(true)
                        isTopFieldsEditedRef.current = true
                        setFormData((prev) => ({ ...prev, benchmark: e.target.value }))
                        scheduleSaveBenchmark('benchmark_estimate', e.target.value)
                      }}
                      disabled={(!isTopFieldsEdited && !isBenchmarkBlank && !(isParamBlank && isZero)) || !isEditable}
                      field="benchmark_estimate"
                      error={
                        isSiteStateValid || formData.benchmark >= 0
                          ? { field: '', error: '' }
                          : {
                            field: 'benchmark_estimate',
                            error: 'pleaseCompleteThisField'
                          }
                      }
                    />
                  </StateFormItemWrapper>
                </StateFormContainer>
              )}

              {formData.previousParameter === false && (
                <>
                  <StateFormContainer>
                    <StateFormItemWrapper>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '1px' }}>
                        <FormattedMessage id="monitoringParameter" defaultMessage="Monitoring parameter" />
                        <Tooltip
                          style={{ zIndex: 10000 }}
                          placement="top"
                          trigger={['hover']}
                          align={{ offset: [0, 16] }}
                          overlay={{
                            label: 'bioMonitoringPerameter',
                            infoTooltip: 'bioMonitoringPerameterToolTip'
                          }}
                          type="big"
                        >
                          <TooltipHintWrapper>
                            <InfoSmallIcon />
                          </TooltipHintWrapper>
                        </Tooltip>
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        <Select
                          placeholder="Select monitoring parameter"
                          options={monitoringParameterOptions}
                          marginBottom="0"
                          value={
                            monitoringParameterOptions.find((o) => o.value === formData.editableMonitoringParameter) ||
                            (formData.editableMonitoringParameter
                              ? {
                                label: formData.editableMonitoringParameter,
                                value: formData.editableMonitoringParameter
                              }
                              : null)
                          }
                          disabled={!isEditable}
                          onChange={(selected) => {
                            scheduleSaveBenchmark('benchmark_parameter_select', selected || { value: '' })
                          }}
                          field="benchmark_parameter"
                          error={
                            isSiteStateValid || formData.editableMonitoringParameter
                              ? { field: '', error: '' }
                              : {
                                field: 'benchmark_parameter',
                                error: 'pleaseCompleteThisField'
                              }
                          }
                          blueBg={true}
                        />

                        {formData.isCustomParameter ? (
                          <div style={{ marginTop: '20px' }}>
                            <Input
                              label="bioBenchmarkValue"
                              placeholder="Value"
                              infoTooltip="benchmarkPerameterTool"
                              value={formData.editableBenchmark}
                              onChange={(e) => scheduleSaveBenchmark('editable_benchmark_estimate', e.target.value)}
                              disabled={!isEditable}
                              field="benchmark_estimate"
                              error={
                                isEmpty
                                  ? {
                                    field: 'benchmark_estimate',
                                    error: 'value cannot be empty'
                                  }
                                  : { field: '', error: '' }
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    </StateFormItemWrapper>

                    <StateFormItemWrapper>
                      {formData.isCustomParameter ? (
                        <div>
                          <Input
                            label="otherParameter"
                            placeholder="enterparameter"
                            value={formData.customMonitoringParameter}
                            marginBottomLabel="12px"
                            onChange={(e) => scheduleSaveBenchmark('custom_benchmark_parameter', e.target.value)}
                            disabled={!isEditable}
                            field="custom_benchmark_parameter"
                            error={
                              formData.customMonitoringParameter?.trim().toLowerCase() === 'other'
                                ? {
                                  field: 'custom_benchmark_parameter',
                                  error: 'other is not acceptable'
                                }
                                : monitoringParameterOptions.find(
                                  (o) =>
                                    o.value &&
                                    formData.customMonitoringParameter &&
                                    o.value.trim().toLowerCase() ===
                                    formData.customMonitoringParameter.trim().toLowerCase()
                                )
                                  ? {
                                    field: 'custom_benchmark_parameter',
                                    error: 'value already exists in the dropdown select from there'
                                  }
                                  : isSiteStateValid || formData.customMonitoringParameter
                                    ? { field: '', error: '' }
                                    : {
                                      field: 'custom_benchmark_parameter',
                                      error: 'pleaseCompleteThisField'
                                    }
                            }
                          />
                        </div>
                      ) : (
                        <Input
                          label="bioBenchmarkValue"
                          placeholder="Value"
                          infoTooltip="benchmarkPerameterTool"
                          value={formData.editableBenchmark}
                          onChange={(e) => scheduleSaveBenchmark('editable_benchmark_estimate', e.target.value)}
                          disabled={!isEditable}
                          marginBottomLabel="12px"
                          field="benchmark_estimate"
                          error={
                            isEmpty
                              ? {
                                field: 'benchmark_estimate',
                                error: 'value cannot be empty'
                              }
                              : { field: '', error: '' }
                          }
                        />
                      )}
                    </StateFormItemWrapper>
                  </StateFormContainer>

                  <FullWidthWrapper>
                    <Textarea
                      label="justifyPerameter"
                      value={formData.justifyBenchmark}
                      onChange={(e) => {
                        const value = e.target.value
                        scheduleSaveBenchmark('justify_benchmark', value)
                      }}
                      isOptional={true}
                      placeholder="enterDetails"
                      disabled={!isEditable}
                      infoTooltip="justifyPerameterTooltip"
                      height="128px"
                      maxLength={MAX_TEXTBOX_CHAR_LIMIT}
                      field="justifyBenchmark"
                      showCharCount={true}
                    />
                  </FullWidthWrapper>
                </>
              )}
            </>
          )}
          <StateEvaluationSection
            evaluationType={evaluationType}
            setEvaluationType={(v) => {
              setEvaluationType(v)
              scheduleSave('state_evaluation_type_id', v)
            }}
            isChangeGenuine={isChangeGenuine}
            setIsChangeGenuine={(v) => {
              setIsChangeGenuine(v)
              scheduleSave('genuine_change', v)
            }}
            benchmarkValue={benchmarkValue}
            setBenchmarkValue={(v) => {
              setBenchmarkValue(v)
              scheduleSave('update_estimate', v)
            }}
            speciesState={speciesState}
            setSpeciesState={(v) => {
              setSpeciesState(v)
              scheduleSave('state_category_extent_id', v)
            }}
            ecosystemState={ecosystemState}
            setEcosystemState={(v) => {
              setEcosystemState(v)
              scheduleSave('state_category_condition_id', v)
            }}
            inferringMethod={inferringMethod}
            setInferringMethod={(v) => {
              setInferringMethod(v)
              const val = v && typeof v === 'object' && 'value' in v ? v.value : v
              scheduleSave('state_category_source_id', val)
            }}
            justification={justification}
            setJustification={(v) => {
              setJustification(v)
              scheduleSave('state_category_justification', v)
            }}
            radioOptions={radioOptions}
            stateOptions={stateOptions}
            inferringOptions={inferringOptions}
            StateEvaluationtyp={evaluationTypeOptionsApi}
            scheduleSave={scheduleSave}
            triggerItem={triggerItem}
            isSiteStateValid={isSiteStateValid}
            isEditable={isEditable}
          />
        </>
      )}
    </div>
  )
}

export default SpeciesStateForm
